import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo, useRef } from 'react'
import { Form, Row, Col } from 'antd'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { useSelector, useDispatch } from 'react-redux'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { useDrawer } from '../../../../core/contexts/DrawerContext.jsx'
import { useLocation } from 'react-router-dom'
import { updateActiveRecord } from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { IntroductionsTable } from '../../../../core/components/IntroductionsTable/index.jsx'
import { ContactRolesTable } from '../tables/ContactRolesTable.tsx'
import { ContactRecordFields, ContactRecord } from '../../../../state_manager/data_models/contact_record.ts'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { getContactInfoFields, getAddressFields } from '../../utils/fieldConfigs.ts'
import { handleFieldChange as handleBusinessRules } from '../../utils/contactFormBusinessRules.ts'
import { useContactsForm } from '../../hooks/useContactsForm.ts'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../../core/components/FormTable/constants.ts'
import { SECTION_CONTACT_OVERVIEW } from '../../../../state_manager/constants/strings.ts'
import { ACTION_TYPE_INSERT } from '../../../../state_manager/globals.ts'

/**
 * What the New drawer is handed back when a tab restores an unsaved contact:
 * the record's fields plus the three nested lists the drawer keeps in its own
 * state and writes into the draft (see the temp-record sync below).
 */
// The drawer's own draft fields ride on the temporary record; they are
// whatever the record holds until the restore effect below checks them.
type ContactDraft = ContactRecord & {
  introductions?: unknown
  prog__current_roles?: unknown
  prog__prior_roles?: unknown
}

export const Drawer = forwardRef<
  { submit: () => Promise<void> },
  { contact?: ContactDraft | null; onSubmit?: (data: any) => Promise<void>; loading?: boolean }
>(({ contact = null, onSubmit, loading = false }, ref) => {
  const dispatch = useDispatch()
  const { isNew, isOpen: drawerIsOpen } = useDrawer()
  const location = useLocation()
  const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()
  const [form] = Form.useForm()

  const tempRecordUuid = useMemo(() => {
    if (isNew) {
      return 'contacts-new'
    }
    return null
  }, [isNew])

  // Drawer-variant layout: true for the URL-driven module drawer AND for the
  // global quick-add overlay (whose DrawerContext override sets isOpen).
  const isDrawerContext = drawerIsOpen || location.pathname.includes('/forms/drawer/')
  const { getLookupOptions } = useLookups()

  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const filterOptions = useSelector((state: any) => state.contacts?.filterOptions)
  const relationshipCategories = useSelector((state: any) => state.globalConfigs?.relationship_categories || [])

  const isUpdatingFromBusinessRules = useRef(false)

  const [introductions, setIntroductions] = useState<any[]>([])
  const introductionsRef = useRef<any>(null)
  const getIntroductionsForAPIRef = useRef<(() => any[]) | null>(null)

  const [currentRoles, setCurrentRoles] = useState<any[]>([])
  const [priorRoles, setPriorRoles] = useState<any[]>([])
  const currentRolesRef = useRef<any>(null)
  const priorRolesRef = useRef<any>(null)

  const getInitialFormData = useCallback(
    (): Partial<ContactRecord> => ({
      [ContactRecordFields.contacts__id]: crypto.randomUUID(),
      [ContactRecordFields.contacts__first_name]: '',
      [ContactRecordFields.contacts__middle_name]: '',
      [ContactRecordFields.contacts__last_name]: '',
      [ContactRecordFields.contacts__preferred_name]: '',
      [ContactRecordFields.contacts__email]: '',
      [ContactRecordFields.contacts__email_other]: '',
      [ContactRecordFields.contacts__email_private]: '',
      [ContactRecordFields.contacts__business_phone]: '',
      [ContactRecordFields.contacts__mobile_phone]: '',
      [ContactRecordFields.contacts__linked_in_url]: '',
      [ContactRecordFields.contacts__address_1]: '',
      [ContactRecordFields.contacts__address_2]: '',
      [ContactRecordFields.contacts__address_3]: '',
      [ContactRecordFields.contacts__address_city_label]: '',
      [ContactRecordFields.contacts__address_stateprovince]: '',
      [ContactRecordFields.contacts__address_postal_code]: '',
      [ContactRecordFields.contacts__address_country_label]: '',
      [ContactRecordFields.contacts__notes]: '',
      prog__current_roles: [],
      prog__prior_roles: []
    }),
    []
  )

  const [formData, setFormData] = useState<Record<string, any>>(() => {
    if (contact && Object.keys(contact).length > 0) {
      const initialData = getInitialFormData()
      const { introductions: _, prog__current_roles: __, prog__prior_roles: ___, ...contactDataWithoutNested } = contact
      return { ...initialData, ...contactDataWithoutNested }
    }
    return getInitialFormData()
  })

  const { transformFormDataToContactRecord, validateRequiredFields } = useContactsForm()

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        return getLookupOptions(category)
      }
    }),
    [getLookupOptions]
  )

  const getCompanyLabelById = useCallback(
    (companyId: string | null | undefined): string => {
      if (!companyId || !filterOptions) return companyId || ''
      const companyOptions = filterOptions['contact_accounts__account_id'] || []
      const option = companyOptions.find((opt: any) => opt.value === companyId)
      return option?.label || option?.name || companyId
    },
    [filterOptions]
  )

  const getCategoryLabelById = useCallback(
    (categoryId: string | null | undefined): string => {
      if (!categoryId) return categoryId || ''

      if (filterOptions) {
        const categoryOptions = filterOptions['contact_accounts__relationship_category_id'] || []
        const option = categoryOptions.find((opt: any) => opt.value === categoryId)
        if (option?.label || option?.name) {
          return option.label || option.name
        }
      }

      if (relationshipCategories && relationshipCategories.length > 0) {
        const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
        if (category?.category) {
          return category.category
        }
      }

      return categoryId
    },
    [filterOptions, relationshipCategories]
  )

  const isInitializingRef = useRef(false)
  const lastSyncedDataRef = useRef<string | null>(null)

  const formDataSyncRef = useRef(formData)
  const introductionsSyncRef = useRef(introductions)
  const currentRolesSyncRef = useRef(currentRoles)
  const priorRolesSyncRef = useRef(priorRoles)

  useEffect(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
      syncTimeoutRef.current = null
    }

    if (isNew && contact && Object.keys(contact).length > 0) {
      isInitializingRef.current = true
      const initialData = getInitialFormData()
      const {
        introductions: rawIntroductions,
        prog__current_roles: rawCurrentRoles,
        prog__prior_roles: rawPriorRoles,
        ...contactDataWithoutNested
      } = contact
      // Only a saved list restores; anything else on the draft is ignored
      const tempIntroductions = Array.isArray(rawIntroductions) ? rawIntroductions : null
      const tempCurrentRoles = Array.isArray(rawCurrentRoles) ? rawCurrentRoles : null
      const tempPriorRoles = Array.isArray(rawPriorRoles) ? rawPriorRoles : null
      const mergedData = { ...initialData, ...contactDataWithoutNested }

      formDataSyncRef.current = mergedData
      if (tempIntroductions) introductionsSyncRef.current = tempIntroductions
      if (tempCurrentRoles) currentRolesSyncRef.current = tempCurrentRoles
      if (tempPriorRoles) priorRolesSyncRef.current = tempPriorRoles

      setFormData(mergedData)
      form.setFieldsValue(mergedData)

      if (tempIntroductions) {
        setIntroductions(tempIntroductions)
      }
      if (tempCurrentRoles) {
        setCurrentRoles(tempCurrentRoles)
      }
      if (tempPriorRoles) {
        setPriorRoles(tempPriorRoles)
      }

      lastSyncedDataRef.current = JSON.stringify({
        ...mergedData,
        introductions: tempIntroductions || [],
        prog__current_roles: tempCurrentRoles || [],
        prog__prior_roles: tempPriorRoles || []
      })

      setTimeout(() => {
        isInitializingRef.current = false
      }, 100)
    } else if (isNew && (!contact || Object.keys(contact).length === 0)) {
      const initialData = getInitialFormData()
      setFormData(initialData)
      form.setFieldsValue(initialData)
      setIntroductions([])
      setCurrentRoles([])
      setPriorRoles([])
      lastSyncedDataRef.current = JSON.stringify({
        ...initialData,
        introductions: [],
        prog__current_roles: [],
        prog__prior_roles: []
      })
    }
  }, [contact, isNew, form, getInitialFormData, tempRecordUuid])

  useEffect(() => {
    formDataSyncRef.current = formData
  }, [formData])
  useEffect(() => {
    introductionsSyncRef.current = introductions
  }, [introductions])
  useEffect(() => {
    currentRolesSyncRef.current = currentRoles
  }, [currentRoles])
  useEffect(() => {
    priorRolesSyncRef.current = priorRoles
  }, [priorRoles])

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (!tempRecordUuid || !isNew || isInitializingRef.current) {
      return
    }

    const currentData = {
      ...formDataSyncRef.current,
      introductions: introductionsSyncRef.current,
      prog__current_roles: currentRolesSyncRef.current,
      prog__prior_roles: priorRolesSyncRef.current
    }
    const currentDataString = JSON.stringify(currentData)

    if (currentDataString === lastSyncedDataRef.current) {
      return
    }

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }

    syncTimeoutRef.current = setTimeout(() => {
      if (!isInitializingRef.current) {
        const latestData = {
          ...formDataSyncRef.current,
          introductions: introductionsSyncRef.current,
          prog__current_roles: currentRolesSyncRef.current,
          prog__prior_roles: priorRolesSyncRef.current
        }
        const latestDataString = JSON.stringify(latestData)

        dispatch(
          updateActiveRecord({
            uuid: tempRecordUuid,
            updates: latestData
          })
        )
        lastSyncedDataRef.current = latestDataString
      }
    }, 300)

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [formData, introductions, currentRoles, priorRoles, tempRecordUuid, isNew, dispatch])

  useEffect(() => {
    if (isDrawerContext) {
      setRightMenuWidth('42%')
      return () => {
        resetRightMenuWidth()
      }
    }
    return undefined
  }, [isDrawerContext, setRightMenuWidth, resetRightMenuWidth])

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      if (isUpdatingFromBusinessRules.current) {
        return
      }

      setFormData((prev) => {
        const businessRuleResult = handleBusinessRules(field, value, prev, form, countriesData)
        const newData = businessRuleResult.formData

        if (businessRuleResult.formUpdates) {
          isUpdatingFromBusinessRules.current = true
          form.setFieldsValue(businessRuleResult.formUpdates)
          setTimeout(() => {
            isUpdatingFromBusinessRules.current = false
          }, 0)
        }

        formDataSyncRef.current = newData

        return newData
      })

      setValidationErrors((prev) => {
        const updated = { ...prev }
        Object.keys(updated).forEach((section) => {
          if (updated[section][field]) {
            const { [field]: removed, ...rest } = updated[section]
            updated[section] = rest
          }
        })
        return updated
      })
    },
    [form, countriesData]
  )

  const handleSubmit = useCallback(async () => {
    try {
      const hasPendingIntroductions = introductionsRef.current?.hasPendingIntroductions?.() || false
      if (hasPendingIntroductions) {
        showErrorToast('Please save or remove any pending introductions before creating the contact', TOAST_CONFIG)
        return
      }

      const hasPendingCurrentRoles = currentRolesRef.current?.hasPendingRoles?.() || false
      if (hasPendingCurrentRoles) {
        showErrorToast('Please save or remove any pending current roles before creating the contact', TOAST_CONFIG)
        return
      }

      const hasPendingPriorRoles = priorRolesRef.current?.hasPendingRoles?.() || false
      if (hasPendingPriorRoles) {
        showErrorToast('Please save or remove any pending prior roles before creating the contact', TOAST_CONFIG)
        return
      }

      const validation = validateRequiredFields(formData)

      const contactInfoFields = getContactInfoFields(formData, helpers)
      const addressFields = getAddressFields(formData, helpers, false)

      const contactInfoErrors: Record<string, string> = {}
      const addressErrors: Record<string, string> = {}

      Object.keys(validation.errors).forEach((fieldKey) => {
        const isContactInfoField = contactInfoFields.some((field) => field.key === fieldKey)
        const isAddressField = addressFields.some((field) => field.key === fieldKey)

        if (isContactInfoField) {
          contactInfoErrors[fieldKey] = validation.errors[fieldKey]
        } else if (isAddressField) {
          addressErrors[fieldKey] = validation.errors[fieldKey]
        } else {
          contactInfoErrors[fieldKey] = validation.errors[fieldKey]
        }
      })

      setValidationErrors({
        contactInfo: contactInfoErrors,
        address: addressErrors
      })

      if (!validation.isValid) {
        return
      }

      const contactData = transformFormDataToContactRecord(formData)

      const isTemporaryKey = (key: string | null | undefined): boolean => {
        if (!key || typeof key !== 'string') return true
        if (key.startsWith('new-') || key.startsWith('intro-') || key.startsWith('role-')) return true
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        return !uuidRegex.test(key)
      }

      const transformedCurrentRoles = (currentRoles || [])
        .filter((role: any) => {
          const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id] || role.companyId
          const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || role.categoryId
          return companyId && categoryId
        })
        .map((role: any) => {
          const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id] || role.companyId
          const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || role.categoryId
          const roleId = role[CompanyTeamEntryFields.contact_accounts__id] || role.key || null
          const roleIdStr = roleId ? String(roleId) : null
          const isTempKey = isTemporaryKey(roleIdStr)

          const roleEntry: any = {
            [CompanyTeamEntryFields.contacts__id]: formData[ContactRecordFields.contacts__id],
            [CompanyTeamEntryFields.contact_accounts__id]: isTempKey ? null : roleIdStr,
            [CompanyTeamEntryFields.contact_accounts__account_id]: companyId,
            [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: categoryId,
            [CompanyTeamEntryFields.contact_accounts__current]: true,
            [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_INSERT
          }

          const roleTypeId = role[CompanyTeamEntryFields.relationship_types__id] || role.roleTypeId
          if (roleTypeId) {
            roleEntry[CompanyTeamEntryFields.relationship_types__id] = roleTypeId
          }
          const title = role[CompanyTeamEntryFields.contact_accounts__title] || role.title
          if (title !== undefined) {
            roleEntry[CompanyTeamEntryFields.contact_accounts__title] = title || null
          }
          const isPrimary = role[CompanyTeamEntryFields.contact_accounts__is_primary] || role.isPrimary
          if (isPrimary === true) {
            roleEntry[CompanyTeamEntryFields.contact_accounts__is_primary] = true
          }

          return roleEntry
        })

      const transformedPriorRoles = (priorRoles || [])
        .filter((role: any) => {
          const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id] || role.companyId
          const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || role.categoryId
          return companyId && categoryId
        })
        .map((role: any) => {
          const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id] || role.companyId
          const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] || role.categoryId
          const roleId = role[CompanyTeamEntryFields.contact_accounts__id] || role.key || null
          const roleIdStr = roleId ? String(roleId) : null
          const isTempKey = isTemporaryKey(roleIdStr)

          const roleEntry: any = {
            [CompanyTeamEntryFields.contacts__id]: formData[ContactRecordFields.contacts__id],
            [CompanyTeamEntryFields.contact_accounts__id]: isTempKey ? null : roleIdStr,
            [CompanyTeamEntryFields.contact_accounts__account_id]: companyId,
            [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: categoryId,
            [CompanyTeamEntryFields.contact_accounts__current]: false,
            [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_INSERT
          }

          const roleTypeId = role[CompanyTeamEntryFields.relationship_types__id] || role.roleTypeId
          if (roleTypeId) {
            roleEntry[CompanyTeamEntryFields.relationship_types__id] = roleTypeId
          }
          const title = role[CompanyTeamEntryFields.contact_accounts__title] || role.title
          if (title !== undefined) {
            roleEntry[CompanyTeamEntryFields.contact_accounts__title] = title || null
          }

          return roleEntry
        })

      contactData.prog__current_roles = transformedCurrentRoles
      contactData.prog__prior_roles = transformedPriorRoles

      if (getIntroductionsForAPIRef.current) {
        const introductionsData = getIntroductionsForAPIRef.current()
        if (introductionsData && introductionsData.length > 0) {
          contactData.prog__introductions = introductionsData
        }
      }

      if (onSubmit) {
        await onSubmit(contactData)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission failed:', error)
      }
      throw error
    }
  }, [formData, currentRoles, priorRoles, onSubmit, validateRequiredFields, transformFormDataToContactRecord, helpers])

  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit
    }),
    [handleSubmit]
  )

  const contactInfoFields = useMemo(() => getContactInfoFields(formData, helpers), [formData, helpers])
  const addressFields = getAddressFields(formData, helpers, false)

  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({
    contactInfo: {},
    address: {}
  })

  return (
    <div
      data-debug='drawer-new-container'
      data-component='ContactDrawerNew'
      data-is-new={isNew}
      data-contact-id={contact?.[ContactRecordFields.contacts__id]}
      className='space-y-1'
    >
      <Form
        data-debug='drawer-new-form'
        form={form}
        layout='vertical'
        className='form-compact'
        initialValues={formData}
        onValuesChange={(changedValues) => {
          if (isUpdatingFromBusinessRules.current) {
            return
          }
          Object.keys(changedValues).forEach((key) => {
            handleFieldChange(key, changedValues[key])
          })
        }}
      >
        {isDrawerContext ? (
          <Row data-debug='drawer-new-main-row' gutter={12}>
            <Col span={12}>
              <FormSection title={SECTION_CONTACT_OVERVIEW} className='mb-2'>
                <InputManager
                  inputMode='edit'
                  labelPosition='above'
                  fields={contactInfoFields}
                  onChange={(field, value) => handleFieldChange(field, value)}
                  validationErrors={validationErrors.contactInfo}
                />
              </FormSection>
            </Col>

            <Col span={12}>
              <FormSection title='Address' className='mb-2'>
                <InputManager
                  inputMode='edit'
                  labelPosition='above'
                  fields={addressFields}
                  onChange={(field, value) => handleFieldChange(field, value)}
                  validationErrors={validationErrors.address}
                />
              </FormSection>
            </Col>
          </Row>
        ) : (
          <Row data-debug='drawer-new-main-row' gutter={12}>
            <Col span={12}>
              <FormSection title={SECTION_CONTACT_OVERVIEW} className='mb-2'>
                <InputManager
                  inputMode='edit'
                  labelPosition='above'
                  fields={contactInfoFields}
                  onChange={(field, value) => handleFieldChange(field, value)}
                  validationErrors={validationErrors.contactInfo}
                />
              </FormSection>
            </Col>

            <Col span={12}>
              <FormSection title='Address' className='mb-2'>
                <InputManager
                  inputMode='edit'
                  labelPosition='above'
                  fields={addressFields}
                  onChange={(field, value) => handleFieldChange(field, value)}
                  validationErrors={validationErrors.address}
                />
              </FormSection>
            </Col>
          </Row>
        )}

        <Row data-debug='drawer-new-current-roles-row' className='mt-2'>
          <Col span={24}>
            <ContactRolesTable
              ref={currentRolesRef}
              mode='current'
              roles={currentRoles}
              onChange={(roles) => setCurrentRoles(roles)}
              contactId={formData[ContactRecordFields.contacts__id]}
              getCompanyLabelById={getCompanyLabelById}
              getCategoryLabelById={getCategoryLabelById}
            />
          </Col>
        </Row>

        <Row data-debug='drawer-new-prior-roles-row' className='mt-2'>
          <Col span={24}>
            <ContactRolesTable
              ref={priorRolesRef}
              mode='prior'
              roles={priorRoles}
              onChange={(roles) => setPriorRoles(roles)}
              contactId={formData[ContactRecordFields.contacts__id]}
              getCompanyLabelById={getCompanyLabelById}
              getCategoryLabelById={getCategoryLabelById}
            />
          </Col>
        </Row>

        <Row data-debug='drawer-new-introductions-row' className='mt-2'>
          <Col span={24}>
            <IntroductionsTable
              ref={introductionsRef}
              mode='new'
              contactId={formData[ContactRecordFields.contacts__id]}
              recordId={formData[ContactRecordFields.contacts__id]}
              initialIntroductions={useMemo(() => {
                if (!introductions || !Array.isArray(introductions)) return []
                return introductions
                  .filter((intro) => intro.companyId && intro.categoryId)
                  .map((intro) => ({
                    [CompanyTeamEntryFields.contact_accounts__id]: intro.key?.startsWith('new-') ? null : intro.key,
                    [CompanyTeamEntryFields.contact_accounts__account_id]: intro.companyId || null,
                    [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: intro.categoryId || null,
                    [CompanyTeamEntryFields.prog__account__name]: intro.companyName || null
                  }))
              }, [introductions])}
              onChange={useCallback((updatedIntroductions: unknown[]) => {
                const validIntroductions = (updatedIntroductions || []).filter(
                  (intro: any) => intro.companyId && intro.categoryId
                )
                setIntroductions(validIntroductions)
              }, [])}
              getCurrentTeamMembers={useCallback((getApiDataFn: () => unknown[]) => {
                getIntroductionsForAPIRef.current = getApiDataFn
              }, [])}
            />
          </Col>
        </Row>
      </Form>
    </div>
  )
})

Drawer.displayName = 'ContactDrawerNew'
